/**
 * AdminLayout — SaaS-style shell used by /owner/* and /hr/* pages.
 *
 * Layout:
 *   - Left sidebar (≥lg screens): logo, role-aware nav, user pill.
 *   - Mobile (<lg): top bar with hamburger toggling a slide-in drawer.
 *   - Main: header strip + children.
 */
import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { menuAllowedForRole } from '../../lib/menu';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ListChecks,
  Users,
  Calendar,
  Wallet,
  FileText,
  Scissors,
  BookOpen,
  RefreshCw,
  Cloud,
  MessageCircle,
  Settings as SettingsIcon,
  UserPlus,
  UserMinus,
  ClipboardCheck,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Home as HomeIcon,
  Clock,
  Banknote,
  ScrollText,
  BadgeCheck,
  BellRing,
  BellOff,
  User as UserIcon,
  Search,
  Pin,
  TrendingUp,
  Grid3x3,
  Sparkles,
  Workflow,
  BarChart3,
  PanelLeftClose,
  PanelLeft,
  CalendarOff,
  CalendarRange,
  Percent,
  Coins,
  Plane,
  ShieldCheck,
  Network,
  UploadCloud,
  Crown,
  FolderArchive,
  MapPin,
  Store,
  Landmark,
} from 'lucide-react';
import { useAuthStore } from '../../lib/store';
import { logout as liffLogout } from '../../lib/liff';
import { ChangelogBadge } from '../ChangelogBadge';
import { Logo } from '../ui/Logo';
import { CloseBanner } from '../CloseBanner';
import { TenantSwitcher } from './TenantSwitcher';
import { useMenuBadgeCounts, type MenuBadgeKey } from '../../hooks';

type AdminRole = 'OWNER' | 'HR' | 'SUPERVISOR';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** If set, only these roles see the item. Otherwise visible to all admins. */
  roles?: AdminRole[];
  /** Pending-count badge key (looked up via useMenuBadgeCounts). */
  badge?: MenuBadgeKey;
}

interface NavGroup {
  id: string;
  title: string;
  items: NavItem[];
}

// IA แบบ moodata: กลุ่ม → เมนูย่อย (ชี้ไป hub-tab จริงผ่าน #hash). หลายเมนูย่อย
// ใต้ path เดียวต้องระบุ #hash เสมอ (ไม่งั้น active state ชนกัน — ดู isActive).
const MENU: NavGroup[] = [
  {
    id: 'overview',
    title: 'ภาพรวม',
    items: [
      { to: '/owner', label: 'แดชบอร์ด', icon: LayoutDashboard, roles: ['OWNER'] },
      { to: '/hr', label: 'แดชบอร์ด', icon: LayoutDashboard, roles: ['HR'] },
      { to: '/hr', label: 'แดชบอร์ด', icon: LayoutDashboard, roles: ['SUPERVISOR'] },
      // อนุมัติคำขอ = action ข้ามโมดูล (ลา/OT/แก้ข้อมูล/ร้องเรียน) → อยู่ภาพรวม ไม่ฝังในกลุ่มเวลา
      { to: '/approvals', label: 'อนุมัติคำขอ', icon: ListChecks, badge: 'approvalsTotal' },
      { to: '/owner/approval-chain', label: 'ตั้งค่าสายอนุมัติ', icon: ListChecks, roles: ['OWNER', 'HR'] },
      { to: '/me/team', label: 'ทีมของฉัน (ใต้บังคับบัญชา)', icon: Users },
      { to: '/team/schedule', label: 'จัดกะทีมงาน', icon: CalendarRange, roles: ['SUPERVISOR'] },
      { to: '/me/requests', label: 'คำขอ & การอนุมัติของฉัน', icon: ListChecks },
    ],
  },
  {
    id: 'people',
    title: 'พนักงาน',
    items: [
      { to: '/hr/people#all', label: 'รายชื่อพนักงาน', icon: Users, roles: ['OWNER', 'HR'] },
      { to: '/hr/people#onboard', label: 'เพิ่มพนักงาน', icon: UserPlus, roles: ['OWNER', 'HR'] },
      { to: '/hr/branches', label: 'สาขา', icon: Store, roles: ['OWNER', 'HR'] },
      { to: '/hr/people#termination', label: 'ลาออก / เลิกจ้าง', icon: UserMinus, roles: ['OWNER', 'HR'] },
      { to: '/hr/people#changes', label: 'คำขอแก้ข้อมูล', icon: ListChecks, roles: ['OWNER', 'HR'] },
      { to: '/owner/hr-permissions', label: 'ตั้งค่าสิทธิ์ HR', icon: ShieldCheck, roles: ['OWNER'] },
    ],
  },
  {
    id: 'certificates',
    title: 'หนังสือรับรอง',
    items: [
      { to: '/owner/certificates', label: 'ออกหนังสือรับรอง', icon: BadgeCheck, roles: ['OWNER', 'HR'] },
      { to: '/owner/documents', label: 'เอกสารพนักงาน', icon: FolderArchive, roles: ['OWNER', 'HR'] },
      { to: '/owner/company-docs', label: 'เอกสารบริษัท', icon: Landmark, roles: ['OWNER', 'HR'] },
      { to: '/hr/jds', label: 'คำบรรยายลักษณะงาน (JD)', icon: FileText, roles: ['OWNER', 'HR'] },
      { to: '/owner/contract-template', label: 'แก้ถ้อยคำสัญญา', icon: FileText, roles: ['OWNER', 'HR'] },
    ],
  },
  {
    id: 'onboarding',
    title: 'รับพนักงานใหม่',
    items: [
      { to: '/owner/onboarding', label: 'ติดตามรับพนักงานใหม่', icon: ClipboardCheck, roles: ['OWNER', 'HR'] },
      { to: '/owner/onboarding-setup', label: 'ตั้งค่าเช็คลิสต์รับพนักงานใหม่', icon: SettingsIcon, roles: ['OWNER', 'HR'] },
    ],
  },
  {
    // SoT (ADR 0003): foundational structure — org (departments/positions) +
    // compensation policy (bands/pay grid/benefits/total-comp/variable pay).
    id: 'structure',
    title: 'กำหนดโครงสร้างองค์กร',
    items: [
      { to: '/owner/org', label: 'โครงสร้างองค์กร', icon: Network, roles: ['OWNER', 'HR'] },
      { to: '/owner/comp', label: 'โครงสร้างค่าตอบแทน', icon: Wallet, roles: ['OWNER', 'HR'] },
    ],
  },
  {
    id: 'time',
    title: 'เวลาทำงาน · ลา · OT',
    // เวลา/วันหยุด/สิทธิ์การลา = /hr/time ทั้งหมด รวมไว้กลุ่มเดียว (เลิกหั่นข้ามกลุ่ม 'ตั้งค่า')
    items: [
      { to: '/hr/time#today', label: 'เข้างานวันนี้', icon: Clock, roles: ['OWNER', 'HR', 'SUPERVISOR'] },
      { to: '/hr/attendance', label: 'ประวัติเข้างานรายคน', icon: Clock, roles: ['OWNER', 'HR', 'SUPERVISOR'] },
      { to: '/hr/time#shifts', label: 'กะการทำงาน', icon: Clock, roles: ['OWNER', 'HR', 'SUPERVISOR'] },
      { to: '/hr/roster-import', label: 'นำเข้าตารางเวร', icon: UploadCloud, roles: ['OWNER', 'HR', 'SUPERVISOR'] },
      { to: '/hr/office-location', label: 'ตั้งค่าพื้นที่ออฟฟิศ (GPS)', icon: MapPin, roles: ['OWNER', 'HR'] },
      { to: '/hr/time#holidays', label: 'ตั้งค่าวันหยุด', icon: CalendarOff, roles: ['OWNER', 'HR'] },
      { to: '/hr/time#leave', label: 'ตั้งค่าสิทธิ์การลา', icon: Plane, roles: ['OWNER', 'HR'] },
    ],
  },
  {
    id: 'payroll',
    title: 'เงินเดือน',
    items: [
      { to: '/owner/cycle', label: 'รอบเงินเดือน', icon: Banknote, roles: ['OWNER', 'HR'] },
      { to: '/owner/payroll/history', label: 'ประวัติการจ่ายเงินเดือน', icon: FileText, roles: ['OWNER', 'HR'] },
      { to: '/owner/wage#allowance', label: 'เงินเพิ่ม / เงินหัก', icon: TrendingUp, roles: ['OWNER', 'HR'] },
      { to: '/hr/commission', label: 'คอมมิชชั่น', icon: Percent, roles: ['OWNER', 'HR'] },
      { to: '/hr/payouts', label: 'จ่ายนอกรอบ (ค่าคอม/OT)', icon: Coins, roles: ['OWNER', 'HR'] },
    ],
  },
  {
    id: 'reports',
    title: 'รายงาน',
    items: [
      { to: '/owner/wage#analysis', label: 'วิเคราะห์ค่าจ้าง', icon: BarChart3, roles: ['OWNER', 'HR'] },
      { to: '/owner/wage#year-end', label: 'สรุปปลายปี / 50 ทวิ', icon: FileText, roles: ['OWNER', 'HR'] },
      { to: '/owner/settings#audit', label: 'ประวัติการใช้งานระบบ', icon: ScrollText, roles: ['OWNER', 'HR'] },
    ],
  },
  {
    id: 'settings',
    title: 'ตั้งค่าระบบ',
    items: [
      { to: '/owner/settings#overview', label: 'ตั้งค่าองค์กร', icon: SettingsIcon, roles: ['OWNER', 'HR'] },
      { to: '/owner/owners', label: 'เจ้าของระบบ (Owner)', icon: Crown, roles: ['OWNER'] },
      { to: '/owner/policies', label: 'นโยบาย / ประกาศการทำงาน', icon: BookOpen, roles: ['OWNER', 'HR'] },
      { to: '/owner/notifications', label: 'ตั้งค่าการแจ้งเตือน', icon: BellOff, roles: ['OWNER', 'HR'] },
      { to: '/owner/line-settings', label: 'ตั้งค่า LINE', icon: MessageCircle, roles: ['OWNER'] },
      { to: '/owner/templates', label: 'เริ่มต้นด้วยเทมเพลต', icon: Sparkles, roles: ['OWNER'] },
    ],
  },
  {
    id: 'guide',
    title: 'คู่มือ & ขั้นตอนการทำงาน',
    items: [
      { to: '/owner/blueprint', label: 'พิมพ์เขียวการทำเงินเดือน', icon: FileText, roles: ['OWNER', 'HR'] },
      { to: '/owner/guide', label: 'ขั้นตอนการทำงาน', icon: Workflow, roles: ['OWNER', 'HR'] },
      { to: '/owner/setup', label: 'ตัวช่วยตั้งค่าระบบ', icon: Sparkles, roles: ['OWNER', 'HR'] },
    ],
  },
];

const COLLAPSE_STORAGE_KEY = 'payroll-edge.menu.collapsed';

function loadCollapsedState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function saveCollapsedState(state: Record<string, boolean>) {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

interface Props {
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Optional right-side header actions (e.g. period selector, refresh button). */
  actions?: ReactNode;
  /** ซ่อนแถบ "ปิดงวด" — ใช้ในหน้าที่มี MonthlyWorkSpine แล้ว (กันซ้ำ) */
  hideCloseBanner?: boolean;
}

export function AdminLayout({ title, subtitle, children, actions, hideCloseBanner }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsedState);
  // ย่อ sidebar เป็น icon-only (จำใน localStorage)
  const [rail, setRail] = useState<boolean>(() => {
    try { return localStorage.getItem('payroll-edge.sidebar.rail') === '1'; } catch { return false; }
  });
  const toggleRail = () => setRail((v) => {
    const n = !v;
    try { localStorage.setItem('payroll-edge.sidebar.rail', n ? '1' : '0'); } catch { /* ignore */ }
    return n;
  });
  // ค้นหา global บน topbar + กด "/" โฟกัส
  const [topSearch, setTopSearch] = useState('');
  const topSearchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault();
        topSearchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  function submitTopSearch(e: React.FormEvent) {
    e.preventDefault();
    navigate(`/find${topSearch.trim() ? `?q=${encodeURIComponent(topSearch.trim())}` : ''}`);
    setTopSearch('');
  }
  const [query, setQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const clear = useAuthStore((s) => s.clear);

  const role = profile?.role ?? 'EMPLOYEE';
  const badgeCounts = useMenuBadgeCounts(role);

  // Keep the sidebar nav scrolled where it was — AdminLayout re-mounts on
  // every navigation, which would otherwise jump the menu back to the top.
  // Callback ref so it works for both the desktop sidebar and mobile drawer
  // (the same Sidebar JSX is rendered twice).
  const navRefCb = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const saved = sessionStorage.getItem('adminNavScroll');
    if (saved) el.scrollTop = Number(saved);
    el.addEventListener('scroll', () => sessionStorage.setItem('adminNavScroll', String(el.scrollTop)), { passive: true });
  }, []);

  function isActive(to: string) {
    // แยก path กับ #hash — เมนูย่อยที่ path เดียวกันแต่ hash ต่าง ต้องไม่ active ชนกัน
    const hi = to.indexOf('#');
    const path = hi >= 0 ? to.slice(0, hi) : to;
    const hash = hi >= 0 ? to.slice(hi) : '';
    let pathMatch: boolean;
    if (path === '/owner' || path === '/hr') pathMatch = location.pathname === path;
    else pathMatch = location.pathname === path || location.pathname.startsWith(path + '/');
    if (!pathMatch) return false;
    if (hash) return location.hash === hash;
    return true;
  }

  function toggleGroup(id: string) {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveCollapsedState(next);
      return next;
    });
  }

  function handleLogout() {
    clear();
    try { liffLogout(); } catch { /* ignore */ }
    navigate('/');
  }

  // MANAGER has the same operational access as HR → treat it as HR for nav.
  const navRole: AdminRole = role === 'MANAGER' ? 'HR' : (role as AdminRole);
  // Pre-filter items by role, drop empty groups, so we don't render headers for groups
  // with no visible items (e.g. HR shouldn't see "เงินเดือน" or "ระบบ").
  const visibleGroups = MENU
    .map((g) => ({
      ...g,
      // 🚨 กรองด้วย role จริง ไม่ใช่ navRole — MANAGER ถูก map เป็น HR ด้านบน
      // จึงเห็นเมนูเงินเดือน/ค่าจ้างทั้งชุดถ้าไม่กรองซ้ำตรงนี้ (หลังบ้านปิดแล้ว = ป้ายโกหก)
      items: g.items.filter(
        (it) => (!it.roles || it.roles.includes(navRole)) && menuAllowedForRole(it.to, role),
      ),
    }))
    .filter((g) => g.items.length > 0);

  // Flat helpers for search + pinned shortcuts.
  const allItems = visibleGroups.flatMap((g) => g.items);
  const q = query.trim().toLowerCase();
  const filtered = q ? allItems.filter((it) => it.label.toLowerCase().includes(q)) : [];
  const pinned = ['/owner/cycle', '/approvals']
    .map((to) => allItems.find((it) => it.to === to))
    .filter((it): it is NavItem => !!it);

  // breadcrumb: แผนผังระบบ › กลุ่ม › หน้า (หาจากเมนูที่ตรงกับ path)
  const activeItem = allItems.find((it) => isActive(it.to));
  const activeGroup = visibleGroups.find((g) => g.items.some((it) => it.to === activeItem?.to));

  const renderItem = (item: NavItem, indent = true) => {
    const active = isActive(item.to);
    const count = item.badge ? badgeCounts[item.badge] : 0;
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={() => setDrawerOpen(false)}
        className={`flex items-center gap-3 ${indent ? 'pl-5 pr-3' : 'px-3'} py-2 rounded-lg text-sm transition relative border-l-[2.5px] ${
          active ? 'border-l-[#7a2350] bg-white text-[#7a2350] font-semibold shadow-sm' : 'border-l-transparent text-[#3a3033] hover:bg-white/45'
        }`}
      >
        <item.icon className={`w-4 h-4 shrink-0 ${active ? 'text-[#1877a3]' : 'text-[#7a7378]'}`} />
        <span className="truncate flex-1">{item.label}</span>
        {count > 0 && (
          <span
            className={`shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold font-mono bg-pink text-white`}
            aria-label={`${count} รายการรออนุมัติ`}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Link>
    );
  };

  const Sidebar = (
    <aside className="w-64 bg-gradient-to-b from-[#eaf7fd] via-[#cbebfa] to-[#e6f2cf] border-r border-[rgba(17,36,50,.08)] flex flex-col h-screen sticky top-0">
      <div className="px-5 py-4 border-b border-[rgba(25,20,23,.07)]">
        <img src="/sukree-logo.png" alt="สุกรีเภสัช" className="h-20 w-auto" />
        <div className="text-[10.5px] font-semibold text-[#a59ba1] uppercase tracking-wider mt-2 mb-3">PayPilot</div>
        <TenantSwitcher />
        {/* HumanAI hub shortcut — same identity, all three apps. Only shown
            to OWNER (typical multi-app role); HR/Supervisor/Employee stay
            put in PayPilot. */}
        {role === 'OWNER' && (
          <Link
            to="/launcher"
            onClick={() => setDrawerOpen(false)}
            className="mt-2 flex items-center gap-2 px-2 py-1.5 text-xs text-[#7a7378] hover:text-[#1877a3] hover:bg-white/35 transition rounded-lg"
          >
            <Grid3x3 className="w-3.5 h-3.5" />
            สลับแอป
          </Link>
        )}
      </div>

      {/* Search */}
      <div className="px-3 pt-3">
        <div className="flex items-center gap-2 rounded-lg bg-white/45 px-2.5 py-1.5 border border-[rgba(25,20,23,.07)]">
          <Search className="w-4 h-4 text-[#7a7378] shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ค้นหาเมนู…"
            className="bg-transparent text-sm w-full outline-none placeholder:text-[#7a7378] text-[#2a2226]"
          />
        </div>
      </div>

      <nav ref={navRefCb} className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
        {q ? (
          filtered.length > 0 ? (
            <div className="space-y-0.5">{filtered.map((it) => renderItem(it, false))}</div>
          ) : (
            <p className="px-3 py-6 text-sm text-muted text-center">ไม่พบเมนู “{query}”</p>
          )
        ) : (
          <>
            {pinned.length > 0 && (
              <div className="pb-1">
                <div className="px-3 py-1.5 text-xs font-semibold text-[#a59ba1] uppercase tracking-wider flex items-center gap-1">
                  <Pin className="w-3 h-3" /> ทางลัด
                </div>
                <div className="space-y-0.5">{pinned.map((it) => renderItem(it, false))}</div>
              </div>
            )}
            {visibleGroups.map((group) => {
              const isCollapsed = !!collapsed[group.id];
              return (
                <div key={group.id} className="pt-1">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className="w-full flex items-center justify-between px-3 py-1.5 rounded text-xs font-semibold text-[#a59ba1] uppercase tracking-wider hover:bg-white/45 transition"
                  >
                    <span>{group.title}</span>
                    {isCollapsed ? (
                      <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                    )}
                  </button>
                  {!isCollapsed && (
                    <div className="mt-0.5 space-y-0.5">
                      {group.items.map((item) => renderItem(item))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        <div className={`pt-4 pb-1 ${q ? 'hidden' : ''}`}>
          <div className="px-3 py-1.5 text-xs font-semibold text-[#a59ba1] uppercase tracking-wider">
            อื่นๆ
          </div>
          <Link
            to="/home"
            onClick={() => setDrawerOpen(false)}
            className="flex items-center gap-3 pl-5 pr-3 py-2 rounded-lg text-sm text-[#3a3033] hover:text-[#1877a3] hover:bg-white/45 transition"
          >
            <HomeIcon className="w-4 h-4 shrink-0" />
            <span>เมนูพนักงาน</span>
          </Link>
          <Link
            to="/profile"
            onClick={() => setDrawerOpen(false)}
            className="flex items-center gap-3 pl-5 pr-3 py-2 rounded-lg text-sm text-[#3a3033] hover:text-[#1877a3] hover:bg-white/45 transition"
          >
            <UserIcon className="w-4 h-4 shrink-0" />
            <span>ประวัติของฉัน</span>
          </Link>
          <Link
            to="/factsheet"
            onClick={() => setDrawerOpen(false)}
            className="flex items-center gap-3 pl-5 pr-3 py-2 rounded-lg text-sm text-[#3a3033] hover:text-[#1877a3] hover:bg-white/45 transition"
          >
            <ScrollText className="w-4 h-4 shrink-0" />
            <span>หลักการคำนวณ</span>
          </Link>
        </div>
      </nav>

      {/* User pill */}
      <div className="border-t border-[rgba(25,20,23,.07)] p-3">
        <button
          onClick={toggleRail}
          className="hidden lg:flex items-center gap-2 w-full px-2 py-1.5 mb-2 text-xs text-[#7a7378] hover:text-[#1877a3] hover:bg-white/45 transition rounded-lg"
        >
          <PanelLeftClose className="w-4 h-4" /> ย่อเมนู
        </button>
        <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-white/45 border border-[rgba(25,20,23,.07)]">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#27AAE1] to-[#1877a3] text-white flex items-center justify-center text-sm font-bold shrink-0">
            {(profile?.display_name ?? profile?.emp_code ?? '?')[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate text-[#2a2226]">{profile?.display_name ?? profile?.emp_code}</div>
            <div className="text-xs text-[#7a7378] truncate">
              {profile?.role} · {profile?.emp_code}
            </div>
          </div>
          <button
            onClick={handleLogout}
            title="ออกจากระบบ"
            className="p-1.5 text-[#7a7378] hover:text-[#b4564f] hover:bg-[rgba(180,86,79,.1)] transition rounded"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <ChangelogBadge />
      </div>
    </aside>
  );

  // sidebar แบบย่อ (icon-only) สำหรับ desktop
  const railSidebar = (
    <aside className="w-16 bg-gradient-to-b from-[#eaf7fd] via-[#cbebfa] to-[#e6f2cf] border-r border-[rgba(17,36,50,.08)] flex flex-col h-screen sticky top-0 items-center">
      <div className="py-4 border-b border-[rgba(25,20,23,.07)] w-full flex justify-center">
        <Logo surface="light" />
      </div>
      <nav className="flex-1 overflow-y-auto py-3 w-full flex flex-col items-center gap-1">
        {allItems.map((it) => {
          const active = isActive(it.to);
          const count = it.badge ? badgeCounts[it.badge] : 0;
          return (
            <Link
              key={it.to}
              to={it.to}
              title={it.label}
              aria-label={it.label}
              className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition border-l-[2.5px] ${
                active ? 'border-l-[#7a2350] bg-white text-[#7a2350] shadow-sm' : 'border-l-transparent text-[#3a3033] hover:bg-white/45'
              }`}
            >
              <it.icon className="w-5 h-5" />
              {count > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-pink" />}
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-[rgba(25,20,23,.07)] p-2 w-full flex justify-center">
        <button onClick={toggleRail} title="ขยายเมนู" aria-label="ขยายเมนู" className="w-10 h-10 flex items-center justify-center rounded-lg text-[#7a7378] hover:text-[#1877a3] hover:bg-white/45 transition">
          <PanelLeft className="w-5 h-5" />
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#F9F5F1] flex">
      {/* Desktop sidebar (ย่อได้) */}
      <div className="hidden lg:block shrink-0">{rail ? railSidebar : Sidebar}</div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <div
        className={`fixed inset-y-0 left-0 z-40 lg:hidden transform transition-transform ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {Sidebar}
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="bg-white/80 backdrop-blur-sm border-b border-[rgba(13,11,10,.1)] sticky top-0 z-20">
          <div className="px-4 lg:px-8 py-3 flex items-center gap-3">
            <button
              onClick={() => setDrawerOpen(!drawerOpen)}
              className="p-2 -ml-2 rounded-lg hover:bg-gray-100 lg:hidden"
            >
              {drawerOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="p-1 rounded-lg hover:bg-gray-100 hidden lg:block"
              title="ย้อนกลับ"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              {/* breadcrumb — แผนผังระบบ / กลุ่ม / หน้า */}
              <nav className="hidden lg:flex items-center gap-1 text-[10.5px] text-[#1877a3] font-semibold uppercase tracking-wider mb-0.5 truncate" aria-label="breadcrumb">
                <Link to={role === 'OWNER' ? '/owner' : '/hr'} className="hover:text-[#1877a3]">แผนผังระบบ</Link>
                {activeGroup && <><ChevronRight className="w-3 h-3" /><span>{activeGroup.title}</span></>}
                <ChevronRight className="w-3 h-3" /><span className="text-[#1877a3] truncate">{title}</span>
              </nav>
              <h1 className="text-lg font-semibold truncate text-[#2a2226]">{title}</h1>
              {subtitle && <p className="text-xs text-[#6b6663] truncate">{subtitle}</p>}
            </div>

            {/* ค้นหา global (กด / โฟกัส) — desktop */}
            <form onSubmit={submitTopSearch} className="hidden lg:flex items-center gap-2 rounded-lg bg-[#fbeaf1] border border-[rgba(25,20,23,.07)] px-3 py-1.5 w-72 shrink-0">
              <Search className="w-4 h-4 text-[#7a7378] shrink-0" />
              <input
                ref={topSearchRef}
                value={topSearch}
                onChange={(e) => setTopSearch(e.target.value)}
                placeholder="ค้นหาพนักงาน เมนู หน้า…"
                className="bg-transparent text-sm w-full outline-none placeholder:text-[#7a7378] text-[#2a2226]"
              />
              <kbd className="text-[10px] text-[#7a7378] border border-[rgba(25,20,23,.1)] bg-white/50 rounded px-1 shrink-0">/</kbd>
            </form>

            {actions && <div className="flex items-center gap-1.5 shrink-0 max-w-[40%] overflow-x-auto">{actions}</div>}
          </div>
        </header>

        {!hideCloseBanner && <CloseBanner />}

        <div className="px-4 lg:px-8 py-4 lg:py-6 max-w-7xl">{children}</div>
      </main>
    </div>
  );
}
